import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { validateEmail, validatePhone, sanitizePhoneInput } from '../utils/validation';
import { countryCodes, pricingPackages, testimonials } from '../mock';
import { ChevronDown, CheckCircle2, Shield, Clock, Users, Search, FileText, Award, MessageCircle } from 'lucide-react';
import PricingCard from '../components/PricingCard';
import TestimonialCard from '../components/TestimonialCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Slider from 'react-slick';
import CountUp from '../components/CountUp';
import RegistrationModal from '../components/RegistrationModal';
import { getGeolocation, submitLead, getDialCodeFromCountryCode } from '../services/apiService';

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    country: 'US',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [geoLocationData, setGeoLocationData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeolocation = async () => {
      const geoData = await getGeolocation();
      setGeoLocationData(geoData);
      
      if (geoData.success && geoData.countryCode) {
        const dialCode = getDialCodeFromCountryCode(geoData.countryCode);
        setFormData(prev => ({
          ...prev,
          countryCode: dialCode,
          country: geoData.countryCode
        }));
      }
    };

    fetchGeolocation();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let sanitizedValue = value;
    
    // Sanitize phone input - only allow digits
    if (name === 'phone') {
      sanitizedValue = sanitizePhoneInput(value);
      // Limit to 10 digits
      if (sanitizedValue.length > 10) {
        sanitizedValue = sanitizedValue.slice(0, 10);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCountryCodeSelect = (code, countryCode) => {
    setFormData(prev => ({
      ...prev,
      countryCode: code,
      country: countryCode
    }));
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const filteredCountryCodes = countryCodes.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.code.includes(searchTerm)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }

    const phoneValidation = validatePhone(formData.phone, formData.countryCode);
    if (!phoneValidation.isValid) {
      newErrors.phone = phoneValidation.error;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    // Prepare data for API
    const leadData = {
      name: formData.name,
      email: formData.email,
      countryDialCode: formData.countryCode,
      country: formData.country,
      phoneNumber: formData.phone, // Only numeric
      description: formData.message
    };

    // Submit to CRM API
    const result = await submitLead(leadData, geoLocationData);

    console.log('Lead submission result:', result);

    setIsSubmitting(false);

    if (result.success) {
      console.log('Redirecting to:', result.redirectUrl);
      // Redirect to application page with customer ID
      window.location.href = result.redirectUrl;
    } else {
      // Show error message
      console.error('Submission failed:', result.error);
      alert(result.error || 'Failed to submit. Please try again.');
    }
  };

  const handleGetStarted = (pkg) => {
    // Open modal
    setIsModalOpen(true);
  };

  // Trademark-specific FAQs
  const trademarkFaqs = [
    {
      id: 1,
      question: 'What is a trademark and why is it essential for my business?',
      answer: 'A trademark is a unique identifier (name, logo, or slogan) that distinguishes your brand from competitors. It provides legal protection, prevents unauthorized use, and builds customer trust and recognition.'
    },
    {
      id: 2,
      question: 'How long does trademark registration take?',
      answer: 'The trademark registration process typically takes 8-12 months from filing to approval, depending on USPTO processing times and any office actions that need to be addressed.'
    },
    {
      id: 3,
      question: 'What are the costs involved in trademark registration?',
      answer: 'Our service starts at just $45, plus USPTO filing fees (typically $250-$350 per class). This covers comprehensive search, filing, and expert guidance throughout the process.'
    },
    {
      id: 4,
      question: 'Can I trademark a business name and logo together?',
      answer: 'Yes! You can register your business name, logo, or both as separate trademarks. We recommend protecting both for comprehensive brand security.'
    },
    {
      id: 5,
      question: 'What happens if someone challenges my trademark application?',
      answer: 'Our expert team handles all USPTO communications, including office actions and oppositions. We prepare professional responses to maximize your approval chances.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Custom Header for Landing Page */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/assets/img/brand/logo-light.png" 
                alt="Filing Protection" 
                className="h-8 sm:h-10 w-auto"
              />
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                onClick={() => setIsModalOpen(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white px-3 sm:px-6 py-2 text-sm sm:text-base transition-all duration-300"
              >
                <span className="hidden sm:inline">Register My Trademark</span>
                <span className="sm:hidden">Register</span>
              </Button>
              <Button
                variant="outline"
                className="chat border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-3 sm:px-6 py-2 text-sm sm:text-base transition-all duration-300"
              >
                <MessageCircle className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Chat Now</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-10 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Form First on Mobile, Right on Desktop */}
            <div id="registration-form" className="order-1 lg:order-2 bg-slate-800/80 backdrop-blur-sm p-6 lg:p-8 rounded-2xl border border-slate-700 shadow-[0_0_50px_rgba(59,130,246,0.3)]">
              <h2 className="text-2xl font-bold text-white mb-2">Register Your Trademark Now</h2>
              <p className="text-gray-400 mb-4 text-sm">Fill out the form and protect your brand today</p>
              <p className="text-blue-400 font-semibold mb-6 text-lg">Special Offer: Only $45 for Trademark Registration</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-slate-900 border ${errors.name ? 'border-red-500' : 'border-slate-600'} rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors`}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-slate-900 border ${errors.email ? 'border-red-500' : 'border-slate-600'} rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <div className="flex gap-2">
                    <div className="relative w-32">
                      <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className={`w-full px-3 py-3 bg-slate-900 border ${errors.phone ? 'border-red-500' : 'border-slate-600'} rounded-lg text-white flex items-center justify-between hover:border-blue-500 transition-colors`}
                      >
                        <span className="text-sm">
                          {countryCodes.find(c => c.code === formData.countryCode)?.flag || '🇺🇸'} {formData.countryCode}
                        </span>
                        <ChevronDown className="w-4 h-4" />
                      </button>

                      {isDropdownOpen && (
                        <div className="absolute z-50 top-full mt-1 w-72 bg-slate-900 border border-slate-600 rounded-lg shadow-xl max-h-64 overflow-hidden">
                          <div className="p-2 border-b border-slate-700">
                            <input
                              type="text"
                              placeholder="Search country..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {filteredCountryCodes.map((country, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handleCountryCodeSelect(country.code, country.country)}
                                className="w-full px-4 py-2 text-left hover:bg-slate-800 text-white text-sm flex items-center gap-2 transition-colors"
                              >
                                <span>{country.flag}</span>
                                <span className="flex-1">{country.name}</span>
                                <span className="text-gray-400">{country.code}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number (10 digits)"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength="10"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={`flex-1 px-4 py-3 bg-slate-900 border ${errors.phone ? 'border-red-500' : 'border-slate-600'} rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors`}
                    />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <textarea
                    name="message"
                    placeholder="Tell us about your trademark needs..."
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    className={`w-full px-4 py-3 bg-slate-900 border ${errors.message ? 'border-red-500' : 'border-slate-600'} rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors resize-none`}
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white py-3 text-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Register Your Trademark Now'}
                </Button>
              </form>
            </div>

            {/* Hero Content - Second on Mobile, First on Desktop */}
            <div className="order-2 lg:order-1 space-y-4 lg:space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 bg-blue-600/20 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-500/30">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-blue-300">Expert Brand Protection You Can Trust</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                Secure Your Brand Identity Today
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed">
                Fast, affordable, and reliable trademark registration services. Protect your business name, logo, and brand identity with our expert legal team starting at just $45.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need This Section */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image First on All Devices */}
            <div className="w-full">
              <img
                src="https://customer-assets.emergentagent.com/job_b22c0509-6262-4f96-a1fd-18a6892f98a6/artifacts/0qtjnfqj_img-1.jpg"
                alt="Trademark Registration Essential"
                className="rounded-xl shadow-2xl w-full"
              />
            </div>

            {/* Content Below Image on Mobile, Right on Desktop */}
            <div className="w-full">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 lg:mb-6 text-center lg:text-left">
                Why Trademark Registration is Essential
              </h2>
              <p className="text-base lg:text-lg text-gray-300 leading-relaxed text-center lg:text-left">
                In today's competitive market, protecting your brand identity is crucial for business success. A registered trademark gives you exclusive legal rights to your brand name, logo, or slogan—preventing competitors from using similar marks that could confuse customers or damage your reputation. Without protection, you risk losing your brand identity and facing costly legal disputes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 lg:mb-6">
              Our Simple 3-Step Process
            </h2>
            <p className="text-base lg:text-lg text-gray-300 max-w-3xl mx-auto">
              Getting your trademark registered has never been easier. Our streamlined process ensures fast, accurate, and hassle-free trademark protection for your brand.
            </p>
          </div>

          {/* Mobile Slider, Desktop Grid */}
          <div className="block md:hidden">
            <Slider
              dots={true}
              infinite={false}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
            >
              {/* Step 1 */}
              <div className="px-2">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg shadow-blue-500/50">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Information Collection</h3>
                  <p className="text-gray-300">Provide your brand details through our secure online form. We gather all necessary information to start your trademark application.</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="px-2">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg shadow-blue-500/50">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Research and Search Report</h3>
                  <p className="text-gray-300">Our experts conduct comprehensive trademark searches and prepare detailed reports to ensure your mark is available for registration.</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="px-2">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg shadow-blue-500/50">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Application and Submission</h3>
                  <p className="text-gray-300">We prepare and file your trademark application with the USPTO, handling all paperwork and communications for official brand protection.</p>
                </div>
              </div>
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8 mt-8 lg:mt-12">
            {/* Step 1 */}
            <div className="group bg-slate-900 border border-slate-700 rounded-xl p-8 text-center transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/30 hover:border-blue-400 hover:bg-gradient-to-br hover:from-slate-900 hover:to-blue-900/20 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg shadow-blue-500/50 transition-all duration-500 group-hover:scale-110 group-hover:shadow-blue-500/70 group-hover:rotate-12">
                1
              </div>
              <h3 className="text-xl font-bold text-white mb-3 transition-colors duration-300 group-hover:text-blue-400">Information Collection</h3>
              <p className="text-gray-300 transition-colors duration-300 group-hover:text-gray-200">Provide your brand details through our secure online form. We gather all necessary information to start your trademark application.</p>
            </div>

            {/* Step 2 */}
            <div className="group bg-slate-900 border border-slate-700 rounded-xl p-8 text-center transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/30 hover:border-blue-400 hover:bg-gradient-to-br hover:from-slate-900 hover:to-blue-900/20 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg shadow-blue-500/50 transition-all duration-500 group-hover:scale-110 group-hover:shadow-blue-500/70 group-hover:rotate-12">
                2
              </div>
              <h3 className="text-xl font-bold text-white mb-3 transition-colors duration-300 group-hover:text-blue-400">Research and Search Report</h3>
              <p className="text-gray-300 transition-colors duration-300 group-hover:text-gray-200">Our experts conduct comprehensive trademark searches and prepare detailed reports to ensure your mark is available for registration.</p>
            </div>

            {/* Step 3 */}
            <div className="group bg-slate-900 border border-slate-700 rounded-xl p-8 text-center transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/30 hover:border-blue-400 hover:bg-gradient-to-br hover:from-slate-900 hover:to-blue-900/20 cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg shadow-blue-500/50 transition-all duration-500 group-hover:scale-110 group-hover:shadow-blue-500/70 group-hover:rotate-12">
                3
              </div>
              <h3 className="text-xl font-bold text-white mb-3 transition-colors duration-300 group-hover:text-blue-400">Application and Submission</h3>
              <p className="text-gray-300 transition-colors duration-300 group-hover:text-gray-200">We prepare and file your trademark application with the USPTO, handling all paperwork and communications for official brand protection.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-400 font-semibold mb-2 text-sm lg:text-base">OUR PRICING</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Affordable Trademark Protection Packages
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 max-w-3xl mx-auto">
              Choose the package that best fits your business needs. All packages include expert guidance, comprehensive support, and guaranteed results. Protect your brand today with our cost-effective solutions.
            </p>
          </div>
          
          {/* Grid Layout for All Devices */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-12">
            {pricingPackages.map((pkg) => (
              <div key={pkg.id}>
                <PricingCard package={pkg} onGetStarted={handleGetStarted} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 lg:mb-6">
              Why Choose Our Trademark Services
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 max-w-3xl mx-auto">
              We've built a reputation for excellence, reliability, and customer satisfaction. Our experienced team ensures your brand receives the highest level of legal protection with personalized service at every step.
            </p>
          </div>

          {/* Mobile: Slider, Desktop: Grid */}
          <div className="block md:hidden mt-8">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
            >
              <div className="px-2">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 lg:p-8">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-3">Expert Legal Protection</h3>
                  <p className="text-sm lg:text-base text-gray-300">Our team of experienced IP attorneys and trademark specialists ensures your brand receives comprehensive legal protection with meticulous attention to detail.</p>
                </div>
              </div>
              <div className="px-2">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 lg:p-8">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-3">Fast 24-Hour Processing</h3>
                  <p className="text-sm lg:text-base text-gray-300">We process your trademark application swiftly within 24 hours, providing efficient service so you can protect your brand without unnecessary delays.</p>
                </div>
              </div>
              <div className="px-2">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 lg:p-8">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-3">98% Success Rate</h3>
                  <p className="text-sm lg:text-base text-gray-300">We guarantee exceptional results with our proven track record. Our 98% approval rate reflects our commitment to excellence and thorough application preparation.</p>
                </div>
              </div>
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8 mt-12">
            {/* USP 1 */}
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 lg:p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/20 hover:border-blue-400">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg lg:text-xl font-bold text-white mb-3">Expert Legal Protection</h3>
              <p className="text-sm lg:text-base text-gray-300">Our team of experienced IP attorneys and trademark specialists ensures your brand receives comprehensive legal protection with meticulous attention to detail.</p>
            </div>

            {/* USP 2 */}
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 lg:p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/20 hover:border-blue-400">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg lg:text-xl font-bold text-white mb-3">Fast 24-Hour Processing</h3>
              <p className="text-sm lg:text-base text-gray-300">We process your trademark application swiftly within 24 hours, providing efficient service so you can protect your brand without unnecessary delays.</p>
            </div>

            {/* USP 3 */}
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 lg:p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/20 hover:border-blue-400">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg lg:text-xl font-bold text-white mb-3">98% Success Rate</h3>
              <p className="text-sm lg:text-base text-gray-300">We guarantee exceptional results with our proven track record. Our 98% approval rate reflects our commitment to excellence and thorough application preparation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Stats Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-6">
              Trusted by Thousands of Businesses
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Stat 1 */}
            <div className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 text-center border border-slate-700 transition-all duration-300 hover:border-blue-500 hover:shadow-xl hover:shadow-blue-500/20">
              <div className="text-6xl font-bold bg-gradient-to-r from-blue-400 to-amber-500 bg-clip-text text-transparent mb-3 transition-transform duration-300 group-hover:scale-110">
                <CountUp end={10000} suffix="+" duration={3000} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Trademarks Filed</h3>
              <p className="text-gray-400">We've successfully filed over 10,000 trademark applications, helping businesses across all industries protect their brand identity.</p>
            </div>

            {/* Stat 2 */}
            <div className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 text-center border border-slate-700 transition-all duration-300 hover:border-blue-500 hover:shadow-xl hover:shadow-blue-500/20">
              <div className="text-6xl font-bold bg-gradient-to-r from-blue-400 to-amber-500 bg-clip-text text-transparent mb-3 transition-transform duration-300 group-hover:scale-110">
                <CountUp end={98} suffix="%" duration={2500} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Approval Success Rate</h3>
              <p className="text-gray-400">Our expert preparation and comprehensive research result in an exceptional 98% approval rate with the USPTO.</p>
            </div>

            {/* Stat 3 */}
            <div className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 text-center border border-slate-700 transition-all duration-300 hover:border-blue-500 hover:shadow-xl hover:shadow-blue-500/20">
              <div className="text-6xl font-bold bg-gradient-to-r from-blue-400 to-amber-500 bg-clip-text text-transparent mb-3 transition-transform duration-300 group-hover:scale-110">
                24/7
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Dedicated Support</h3>
              <p className="text-gray-400">Our customer support team is available around the clock to assist you at every step of your trademark registration journey.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section className="py-12 lg:py-20 relative overflow-hidden text-white">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_b22c0509-6262-4f96-a1fd-18a6892f98a6/artifacts/aerejfv7_cta-bg.jpg)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-slate-900/95"></div>
        
        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
            Don't Risk Your Brand's Future!
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 lg:mb-8 leading-relaxed">
            Unregistered trademarks leave your brand vulnerable to theft and legal disputes. Protect your business identity with our expert trademark registration services—fast, reliable, and affordable. Secure your exclusive rights today before it's too late!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-amber-500 hover:to-amber-600 text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg shadow-xl transition-all duration-300 w-full sm:w-auto"
            >
              Protect Your Brand Now
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-400 font-semibold mb-2 text-sm lg:text-base">TESTIMONIALS</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
          </div>
          
          <Slider
            dots={true}
            infinite={true}
            speed={500}
            slidesToShow={3}
            slidesToScroll={1}
            autoplay={true}
            autoplaySpeed={4000}
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 1,
                }
              },
              {
                breakpoint: 640,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1
                }
              }
            ]}
          >
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="px-4">
                <TestimonialCard testimonial={testimonial} />
              </div>
            ))}
          </Slider>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-400 font-semibold mb-2 text-sm lg:text-base">FAQs</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            {trademarkFaqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold text-white text-sm lg:text-base">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 text-sm lg:text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 text-sm text-gray-400">
            <p>© {new Date().getFullYear()} Filing Protection. All rights reserved.</p>
            <div className="flex gap-4">
              <a href="/privacy-policy" className="hover:text-blue-400 transition-colors">
                Privacy Policy
              </a>
              <span>|</span>
              <a href="/terms-conditions" className="hover:text-blue-400 transition-colors">
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Registration Modal */}
      <RegistrationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default LandingPage;